import * as cellStyles from "./DataTableCell.css";
import * as rowStyles from "./DataTableRow.css";
import * as styles from "./DataTableSkeleton.css";

import { clsx } from "clsx";
import type { JSX } from "react";

import { Skeleton } from "@frontend/components/atoms/Skeleton";
import type { SortingState } from "@tanstack/react-table";

import type {
  DataTableSize,
  DataTableSpacing,
  DataTableVariant,
} from "./DataTable.types";
import { dataTableSpacingValue } from "./cellSizeStyles";

type DataTableSkeletonProps = {
  rows: number;
  rowHeight?: number | string;
  columns: string[];
  selectable?: boolean;
  sorting: SortingState;
  gridTemplateColumns: string;
  size: DataTableSize;
  variant: DataTableVariant;
  columnBorders?: boolean;
  striped?: boolean;
  horizontalSpacing: DataTableSpacing;
  verticalSpacing: DataTableSpacing;
};

const BAR_WIDTHS = ["72%", "45%", "88%", "56%", "35%", "64%"];

export function DataTableSkeleton({
  rows,
  rowHeight,
  columns,
  selectable,
  sorting,
  gridTemplateColumns,
  size,
  variant,
  columnBorders,
  striped,
  horizontalSpacing,
  verticalSpacing,
}: DataTableSkeletonProps): JSX.Element[] {
  return Array.from({ length: rows }, (_, rowIndex) => (
    <div
      key={rowIndex}
      role="row"
      aria-hidden="true"
      className={rowStyles.row({ variant, striped })}
      style={{
        gridTemplateColumns,
        minHeight: typeof rowHeight === "number" ? `${rowHeight}px` : rowHeight,
      }}
    >
      {selectable ? <div className={cellStyles.cell({ size })} /> : null}
      {columns.map((columnId, columnIndex) => (
        <div
          key={columnId}
          className={cellStyles.cell({
            size,
            columnBorders,
            sorted: sorting.some((entry) => entry.id === columnId),
          })}
        >
          <div
            className={clsx(cellStyles.content({ size }), styles.content)}
            style={{
              paddingInline: dataTableSpacingValue[horizontalSpacing],
              paddingBlock: dataTableSpacingValue[verticalSpacing],
            }}
          >
            <div className={styles.line}>
              <Skeleton
                radius="full"
                effect="none"
                className={styles.bar}
                style={{
                  width:
                    BAR_WIDTHS[(rowIndex + columnIndex) % BAR_WIDTHS.length],
                }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  ));
}
